import React, { useEffect, useState } from "react";
import { Alert, Box, Button, Modal } from "@mui/material";
import { Button as Btn, Typography } from "@material-ui/core";
import { ToastContainer } from "react-toastify";
import { Spinner } from "../../components";
import { DatabaseEntities } from "../../model/enum/database-entities.enum";
import AlertService from "../../service/AlertService";
import ApiService from "../../service/ApiService";
import Appointment from "../../model/appointment";
import Project from "../../model/project";
import Status from "../../model/status";
import CloseIcon from "@mui/icons-material/Close";
import Misc from "../../utils/Misc";
import useStyles from "./styles";

const style = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "25rem !important",
  bgcolor: "background.paper",
  border: "none",
  borderRadius: "8px",
  boxShadow: 24,
  pt: 2,
  px: 4,
  pb: 3,
  maxHeight: "90vh", // Restrict maximum height to 80% of the viewport height
  overflowY: "auto", // Enable vertical scrolling
  "@media (max-width: 640px)": {
    width: "80%",
  },
};

interface Props {
  appointment: Appointment;
  resetList?: () => void;
}

const UpdateStatusModal = ({ appointment, resetList }: Props) => {
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const classes = useStyles();

  const [statusId, setStatusId] = useState(appointment.statusId);
  const [statuses, setStatuses] = useState<Status[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await ApiService.get<Status[]>(`${DatabaseEntities.STATUS}?type=General`)
        .then((data) => {
          setStatuses(data);
        })
        .finally(() => {
          setLoading(false);
        });
    };

    if (open && Misc.isArrayEmpty(statuses)) fetchData();
  });

  const handleStatus = (e: any) => setStatusId(e.target.value);

  const invalid = () => {
    return statusId === null || statusId === appointment.statusId;
  };

  const handleSubmit = async () => {
    if (invalid()) {
      AlertService.error("Please select a valid status");
      return;
    }

    setLoading(true);
    try {
      await ApiService.put<Appointment>(
        `${DatabaseEntities.APPOINTMENTS}/${appointment.id}`,
        { statusId },
      ).then((data) => {
        appointment = data;
        AlertService.success("Project status updated successfully");
      });
    } catch (error) {
      console.error("Error updating project status:", error);
    } finally {
      setLoading(false);
      resetList?.();
      handleClose();
    }
  };

  return (
    <div>
      <Btn onClick={handleOpen} className={classes.updateButton}>
        Update status
      </Btn>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        sx={{ overflowY: "auto" }}
      >
        {loading ? (
          <Spinner />
        ) : (
          <Box sx={style}>
            <div className={classes.headerDiv}>
              <div className={classes.updateHeaderSubDiv}>
                <Typography className={classes.title}>Appointment</Typography>
                <div
                  className="vl"
                  style={{ marginLeft: "1rem", marginRight: "1rem" }}
                ></div>
                <Typography className={classes.subtitle}>
                  {appointment.id}
                </Typography>
              </div>
              <Button onClick={handleClose} sx={{ color: "black" }}>
                <CloseIcon />
              </Button>
            </div>
            <hr />
            <div className={classes.updateForm}>
              <Typography
                style={{
                  marginBottom: "1rem",
                  color: "#747477",
                  fontWeight: "bold",
                  fontSize: "1.2rem",
                }}
              >
                Update the appointment status
              </Typography>
              <label className={classes.label}>Status</label>
              <select
                className={classes.updateInput}
                value={statusId}
                onChange={handleStatus}
              >
                <option value="">Select status</option>
                {statuses.map((status: Status) => {
                  return (
                    <option key={status.id} value={status.id}>
                      {status.name}
                    </option>
                  );
                })}
              </select>
              <div className={classes.buttonDiv}>
                <Btn
                  className={classes.updateSubmitButton}
                  onClick={handleSubmit}
                  disabled={invalid()}
                >
                  Update
                </Btn>
              </div>
            </div>
          </Box>
        )}
      </Modal>
      <ToastContainer />
    </div>
  );
};

export default UpdateStatusModal;
